import { useState, useCallback, useRef } from 'react';
import { ArrowRight, BarChart3, GalleryHorizontal, UtensilsCrossed, Upload, X, FileText } from 'lucide-react';
import { ACCEPT_ATTRIBUTE as ACCEPTED_FILE_TYPES } from '@optidev/shared-types';
import { validateFilesForUpload } from '@/lib/files/image-validation';
import toast from 'react-hot-toast';
import { getActiveBridge, CHILD_ACTION } from './OptiSignsBridge';
import { setPendingFiles } from './pending-files';
import { useEmbedStore } from '@/stores/embed-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useEmbedSubscriptionGate } from './useEmbedSubscriptionGate';
import { EmbedSubscriptionBlockedModal } from './EmbedSubscriptionBlockedModal';
import { cn } from '@/lib/utils';

const EXAMPLE_CHIPS = [
  'Turn my spreadsheet into a polished dashboard for our lobby screens',
  'Redesign my PDF safety manual as a clean, readable signage display',
  'Build a patient waiting room kiosk showcasing our doctors',
  'Create a welcome screen for our office with live weather and news',
];

// 4 example screens with gradient fallbacks
const CAROUSEL_ITEMS = [
  {
    label: 'Presentation Slides',
    gradient: 'from-teal-500 to-cyan-500',
    icon: GalleryHorizontal,
    image: '/optidev/presentation-slides.jpg',
  },
  {
    label: 'Manufacturing Dashboard',
    gradient: 'from-blue-500 to-indigo-600',
    icon: BarChart3,
    image: '/optidev/manufacturing-dashboard.jpg',
  },
  {
    label: 'Analytics Dashboard',
    gradient: 'from-purple-500 to-fuchsia-500',
    icon: BarChart3,
    image: '/optidev/analytics-dashboard.jpg',
  },
  {
    label: 'Feedback Kiosk',
    gradient: 'from-orange-500 to-red-500',
    icon: UtensilsCrossed,
    image: '/optidev/feedback-kiosk.jpg',
  },
];

interface ProvisionResponse {
  ok: boolean;
  workspaceId?: string;
  error?: string;
}

/**
 * Onboarding shown inside the OptiDev iframe when no workspace is linked yet.
 *
 * Flow:
 * 1. User types a prompt, optionally attaches files, clicks the arrow button
 * 2. sendRequest('PROVISION_WORKSPACE') → scio3 calls OptiPrime provision API
 * 3. On success: stash files in pending-files, update workspace store, set prompt in URL
 * 4. Clear needsProvisioning → ProtectedRoute renders DashboardLayout → Dashboard reads prompt + files
 */
export default function EmbedOnboarding() {
  const { isBlocked: isEmbedBlocked } = useEmbedSubscriptionGate();
  const [prompt, setPrompt] = useState('');
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [showTrialConfirm, setShowTrialConfirm] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Triple items for seamless infinite carousel loop
  const carouselItems = [...CAROUSEL_ITEMS, ...CAROUSEL_ITEMS, ...CAROUSEL_ITEMS];

  const isImageFile = (file: File) => file.type.startsWith('image/');

  const addValidatedFiles = useCallback(async (rawFiles: File[]) => {
    const { valid, invalid } = await validateFilesForUpload(rawFiles);

    if (invalid.length > 0) {
      invalid.forEach(({ file, reason }) => {
        toast.error(`${file.name}: ${reason}`, { duration: 4000 });
      });
    }

    if (valid.length > 0) {
      setFiles(prev => [...prev, ...valid]);
    }
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    await addValidatedFiles(Array.from(selected));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addValidatedFiles]);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleCreateClick = useCallback(() => {
    const trimmed = prompt.trim();
    if (!trimmed || isProvisioning) return;
    setShowTrialConfirm(true);
  }, [prompt, isProvisioning]);

  const handleConfirmTrial = useCallback(async () => {
    setShowTrialConfirm(false);
    const trimmed = prompt.trim();
    if (!trimmed || isProvisioning) return;

    setIsProvisioning(true);
    setError('');

    try {
      const bridge = getActiveBridge();
      if (!bridge) throw new Error('Bridge not available');

      const response = await bridge.sendRequest<ProvisionResponse>(
        CHILD_ACTION.PROVISION_WORKSPACE,
      );

      if (!response.ok || !response.workspaceId) {
        throw new Error(response.error || 'Provisioning failed');
      }

      // Stash files for Dashboard to pick up and upload after mount
      if (files.length > 0) {
        setPendingFiles(files);
      }

      // Update workspace store — also syncs ?ws= in URL via history.replaceState
      useWorkspaceStore.getState().setActiveWorkspaceId(response.workspaceId);

      // Add prompt params directly via history API (not navigate()).
      // BrowserRouter uses v7_startTransition which defers navigate() updates,
      // so Dashboard would mount before the URL has the prompt params.
      // history.replaceState is synchronous — guaranteed visible on next mount.
      const url = new URL(window.location.href);
      url.searchParams.set('prompt', btoa(encodeURIComponent(trimmed)));
      url.searchParams.set('autoSubmit', 'true');
      window.history.replaceState({}, '', url.toString());

      // Clear provisioning flag — ProtectedRoute will now render DashboardLayout
      // Dashboard reads prompt from window.location.search on mount
      useEmbedStore.getState().setNeedsProvisioning(false);
    } catch (err) {
      console.error('[EmbedOnboarding] provision error:', err);
      setError('Something went wrong. Please try again.');
      setIsProvisioning(false);
    }
  }, [prompt, isProvisioning, files]);

  const handleCancelTrial = useCallback(() => {
    setShowTrialConfirm(false);
  }, []);

  const handleChipClick = useCallback((chip: string) => {
    setPrompt(chip);
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && prompt.trim() && !isProvisioning) {
        e.preventDefault();
        handleCreateClick();
      }
    },
    [prompt, isProvisioning, handleCreateClick],
  );

  if (isProvisioning) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-purple-500" />
          <p className="text-sm text-muted-foreground">Setting up your workspace...</p>
        </div>
      </div>
    );
  }

  if (isEmbedBlocked) {
    return <EmbedSubscriptionBlockedModal />;
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <style>{`
        @keyframes embed-marquee {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-33.3333%, 0, 0); }
        }
        .embed-marquee {
          animation: embed-marquee 32s linear infinite;
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        .embed-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* ── Top: headline + input ── */}
      <div className="flex shrink-0 flex-col items-center px-6 pb-32 pt-32">
        <div className="w-full max-w-xl text-center">
          <h1 className="mb-1.5 text-3xl font-semibold tracking-tight">
            Build for your screens with AI
          </h1>
          <p className="mb-6 text-sm text-muted-foreground">
            Create kiosks, dashboards, and custom displays. Publish to any screen in one click.
          </p>

          {/* Prompt input */}
          <div className="relative rounded-2xl border border-border bg-card shadow-sm focus-within:border-purple-500/50 focus-within:ring-1 focus-within:ring-purple-500/20">
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe what you want to create..."
              rows={3}
              className={cn(
                'w-full resize-none rounded-2xl bg-transparent px-4 pt-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none',
                files.length > 0 ? 'pb-[4.5rem]' : 'pb-14',
              )}
            />

            {/* File thumbnails row — between textarea and bottom bar */}
            {files.length > 0 && (
              <div className="absolute bottom-12 left-3 right-14 flex gap-2 px-1 pt-1 pb-0.5" style={{ scrollbarWidth: 'none' }}>
                {files.map((file, index) => {
                  const isImage = isImageFile(file);
                  const previewUrl = isImage ? URL.createObjectURL(file) : '';
                  return (
                    <div key={`${file.name}-${index}`} className="relative group/thumb flex-shrink-0 mt-1 mr-1">
                      <div className="relative w-10 h-10 bg-muted rounded-lg border border-border overflow-hidden flex items-center justify-center">
                        {isImage && previewUrl ? (
                          <img src={previewUrl} alt={file.name} className="w-full h-full object-cover" />
                        ) : (
                          <FileText className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(index)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-900/95 hover:bg-red-600 dark:bg-gray-800/95 dark:hover:bg-red-600
                                 rounded-full flex items-center justify-center opacity-0 group-hover/thumb:opacity-100
                                 transition-all shadow-lg border border-white/30 z-10"
                        title="Remove"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Bottom bar: attach icon + credits on left, submit on right */}
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  title="Attach files"
                >
                  <Upload className="h-4 w-4" />
                </button>
                <span className="text-xs text-muted-foreground/40 leading-none">·</span>
                <span className="text-xs text-muted-foreground/80">100 free credits</span>
                <span className="text-xs text-muted-foreground/40 leading-none">·</span>
                <span className="text-xs text-muted-foreground/80">14-day free trial</span>
              </div>

              <button
                onClick={handleCreateClick}
                disabled={!prompt.trim() || isProvisioning}
                aria-label="Create"
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-lg transition-all',
                  prompt.trim()
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'cursor-not-allowed bg-muted text-muted-foreground',
                )}
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Example chips — 2x2 grid */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            {EXAMPLE_CHIPS.map((chip) => (
              <button
                key={chip}
                onClick={() => handleChipClick(chip)}
                className="rounded-xl border border-border bg-card px-2.5 py-1.5 text-left text-[10px] leading-tight text-muted-foreground transition-colors hover:border-purple-500/40 hover:bg-purple-500/5 hover:text-foreground"
              >
                {chip}
              </button>
            ))}
          </div>

          {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
        </div>
      </div>

      {/* Trial confirmation modal */}
      {showTrialConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-card p-5 shadow-xl border border-border">
            <h3 className="text-lg font-semibold">Try OptiDev free for 14 days</h3>
            <ul className="mt-2.5 space-y-1 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>100 free AI credits to get started</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Cancel anytime — no charge during trial</span>
              </li>
            </ul>
            <div className="mt-3 rounded-lg bg-muted/50 px-3 py-2.5 text-xs text-muted-foreground/70 space-y-0.5">
              <p>After trial: <span className="text-foreground/70 font-medium">$30/mo</span> base · 3 screens · 100 credits/mo</p>
              <p>Extra screens: <span className="text-foreground/70 font-medium">$10/screen/mo</span> · +40 credits each</p>
            </div>
            <div className="mt-4 flex gap-3 justify-end">
              <button
                onClick={handleCancelTrial}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
              >
                Not now
              </button>
              <button
                onClick={handleConfirmTrial}
                className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 transition-colors"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bottom: carousel ── */}
      <div className="flex min-h-0 flex-1 flex-col pb-6">
        <p className="mb-3 text-center text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Examples
        </p>
        <div
          className="flex-1 overflow-hidden"
          style={{
            maskImage:
              'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
          }}
        >
          <div className="embed-marquee flex h-full w-max">
            {carouselItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="group relative shrink-0 cursor-default overflow-hidden rounded-2xl border border-white/10 shadow-lg transition-transform duration-300 hover:scale-[1.02]"
                  style={{ width: 420, aspectRatio: '16/10', marginRight: 16 }}
                >
                  {/* Gradient fallback — always underneath */}
                  <div
                    className={cn(
                      'absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br text-white',
                      item.gradient,
                    )}
                  >
                    <Icon className="h-12 w-12 opacity-80" />
                    <span className="text-sm font-medium opacity-90">{item.label}</span>
                  </div>
                  {/* Actual screenshot — hides on error, gradient shows through */}
                  <img
                    src={item.image}
                    alt={item.label}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-4">
                    <span className="text-sm font-semibold text-white drop-shadow">
                      {item.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
